import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AppWindow, Loader2, Check, Ban, Minus } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { value: "default", label: "Default", icon: Minus },
  { value: "allow", label: "Allow", icon: Check },
  { value: "deny", label: "Deny", icon: Ban },
];

function Segmented({ value, onChange, appId }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
      {OPTIONS.map((o) => {
        const active = value === o.value;
        const tone = o.value === "allow" ? "bg-emerald-500 text-white" : o.value === "deny" ? "bg-red-500 text-white" : "bg-slate-200 text-slate-700";
        return (
          <button key={o.value} type="button" data-testid={`assign-app-${appId}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-medium transition-colors ${active ? tone : "bg-white text-slate-400 hover:bg-slate-50"}`}>
            <o.icon className="h-3 w-3" /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function AppAssignDialog({ scope, target, open, onClose }) {
  const base = scope === "org" ? "organizations" : "users";
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !target?.id) return;
    setLoading(true);
    api.get(`/${base}/${target.id}/apps`)
      .then((r) => setApps(r.data))
      .catch(() => toast.error("Could not load apps."))
      .finally(() => setLoading(false));
  }, [open, target?.id, base]);

  const setState = (id, state) => setApps((prev) => prev.map((a) => (a.app_id === id ? { ...a, state } : a)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/${base}/${target.id}/apps`, {
        grants: apps.filter((a) => a.state === "allow").map((a) => a.app_id),
        denies: apps.filter((a) => a.state === "deny").map((a) => a.app_id),
      });
      toast.success("App access updated");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AppWindow className="h-5 w-5 text-primary" /> App access — {target?.name}</DialogTitle>
          <DialogDescription>
            {scope === "org"
              ? "Allow or deny apps for every user in this organization, overriding the role defaults. Leave on Default to inherit the role setting."
              : "Allow or deny apps for this person on top of their audience and role defaults. Allow adds the app's view permission to their account, Deny revokes it. Leave on Default to inherit."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
        ) : (
          <div className="max-h-[26rem] space-y-1 overflow-auto rounded-lg border border-slate-200 p-2" data-testid="app-assign-list">
            {apps.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400">No apps available.</div>
            ) : apps.map((a) => (
              <div key={a.app_id} className="flex items-center gap-2 rounded px-1.5 py-1.5 text-sm hover:bg-slate-50">
                <span className="flex-1 truncate text-slate-600">
                  {a.name}
                  {a.app_type === "native" && <span className="ml-1.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">Built-in</span>}
                  {scope === "user" && (
                    <span className="ml-1.5 text-[10px] text-slate-400">({a.inherited ? "shown by default" : "hidden by default"})</span>
                  )}
                </span>
                <Segmented value={a.state || "default"} appId={a.app_id} onChange={(s) => setState(a.app_id, s)} />
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-app-assign-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
